import { KnowledgeDocumentVersion } from '@/types/knowledge';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useMemo } from 'react';
import DiffMatchPatch from 'diff-match-patch';

interface VersionDiffProps {
  version1: KnowledgeDocumentVersion;
  version2: KnowledgeDocumentVersion;
}

export function VersionDiff({ version1, version2 }: VersionDiffProps) {
  const dmp = useMemo(() => new DiffMatchPatch(), []);

  const renderDiff = (text1: string, text2: string) => {
    const diffs = dmp.diff_main(text1, text2);
    dmp.diff_cleanupSemantic(diffs);

    return (
      <div className="whitespace-pre-wrap font-mono text-sm">
        {diffs.map((diff, index) => {
          const [operation, text] = diff;

          if (operation === 0) {
            // No change
            return <span key={index}>{text}</span>;
          } else if (operation === 1) {
            // Addition
            return (
              <span key={index} className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
                {text}
              </span>
            );
          } else {
            // Deletion
            return (
              <span key={index} className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 line-through">
                {text}
              </span>
            );
          }
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Versão {version1.version}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">
            <p>Protocolo: {version1.sourceProtocol}</p>
            {version1.editReason && <p>Motivo: {version1.editReason}</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Versão {version2.version}</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground">
            <p>Protocolo: {version2.sourceProtocol}</p>
            {version2.editReason && <p>Motivo: {version2.editReason}</p>}
          </CardContent>
        </Card>
      </div>

      {version1.category !== version2.category && (
        <Card>
          <CardHeader>
            <CardTitle>Categoria</CardTitle>
          </CardHeader>
          <CardContent>
            {renderDiff(version1.category, version2.category)}
          </CardContent>
        </Card>
      )}

      {version1.title !== version2.title && (
        <Card>
          <CardHeader>
            <CardTitle>Título</CardTitle>
          </CardHeader>
          <CardContent>
            {renderDiff(version1.title, version2.title)}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Problema</CardTitle>
        </CardHeader>
        <CardContent>
          {renderDiff(version1.problem, version2.problem)}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Solução</CardTitle>
        </CardHeader>
        <CardContent>
          {renderDiff(version1.solution, version2.solution)}
        </CardContent>
      </Card>
    </div>
  );
}
